/**
 * Helper for displaying and managing record IDs for workflow testing
 */

/**
 * Get all records with IDs for a given object type
 */
export function getRecordsWithIds(objectApiName: string): Array<{ id: string; label: string }> {
  const storageKey = getStorageKey(objectApiName);
  const data = localStorage.getItem(storageKey);

  if (!data) {
    return [];
  }

  try {
    const records = JSON.parse(data);

    return records.map((record: any) => ({
      id: record.id,
      label: getRecordLabel(record, objectApiName)
    }));
  } catch (error) {
    console.error('Error parsing records:', error);
    return [];
  }
}

/**
 * Get human-readable label for a record
 */
function getRecordLabel(record: any, objectApiName: string): string {
  // Try common name fields
  if (record.name) return `${record.name} (${record.id})`;
  if (record.subject) return `${record.subject} (${record.id})`;
  if (record.title) return `${record.title} (${record.id})`;
  if (record.caseNumber) return `Case ${record.caseNumber} (${record.id})`;

  // Fallback to ID only
  return record.id;
}

/**
 * Get storage key for object type
 */
function getStorageKey(objectApiName: string): string {
  const keyMap: Record<string, string> = {
    'Client': 'crm_client_records',
    'Lead': 'crm_lead_records',
    'Contact': 'crm_contact_records',
    'Opportunity': 'crm_opportunity_records',
    'Case': 'crm_case_records',
    'Task': 'crm_task_records',
    'Event': 'crm_event_records',
    'Report': 'crm_report_records'
  };

  return keyMap[objectApiName] || `crm_${objectApiName.toLowerCase()}_records`;
}

/**
 * Ensure a record has an ID
 */
export function ensureRecordId(record: any, objectApiName: string): any {
  if (!record.id) {
    record.id = generateRecordId(objectApiName);
  }
  return record;
}

/**
 * Generate a record ID
 */
export function generateRecordId(objectApiName: string): string {
  const prefix = objectApiName.toLowerCase();
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}_${timestamp}_${random}`;
}

/**
 * Fix all records in storage to have IDs
 */
export function fixAllRecordIds(): void {
  const objects = ['Client', 'Lead', 'Contact', 'Opportunity', 'Case', 'Task', 'Event', 'Report'];

  let totalFixed = 0;

  objects.forEach(objectApiName => {
    const storageKey = getStorageKey(objectApiName);
    const data = localStorage.getItem(storageKey);

    if (!data) return;

    try {
      const records = JSON.parse(data);
      let fixed = 0;

      const updatedRecords = records.map((record: any) => {
        if (!record.id) {
          record.id = generateRecordId(objectApiName);
          fixed++;
        }
        return record;
      });

      if (fixed > 0) {
        localStorage.setItem(storageKey, JSON.stringify(updatedRecords));
        console.log(`✅ Fixed ${fixed} ${objectApiName} records`);
        totalFixed += fixed;
      }
    } catch (error) {
      console.error(`Error fixing ${objectApiName} records:`, error);
    }
  });

  console.log(`✅ Total records fixed: ${totalFixed}`);
}
